import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Check, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { PageHero } from "../components/site/PageHero";
import { Eyebrow, Reveal } from "../components/site/Reveal";
import { ENTITY_TYPES } from "../data/services";
import { FIRM } from "../data/firm";

const field = "w-full bg-transparent border-b border-ink/25 focus:border-ink outline-none py-3.5 placeholder:text-ink/50 transition-colors";

export default function Contact() {
  const { state } = useLocation();
  const empty = { name: "", email: "", phone: "", company: "", entity_type: "", message: state?.entity ? `Enquiry regarding ${state.entity} compliance.` : "How can we help? *" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", f);
      setSent(true);
      toast.success("Enquiry received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHero testid="contact-hero" eyebrow="Contact" lines={["Let's talk", <span className="font-serif-i text-brass">compliance.</span>]} intro="Tell us about your registration and what you need. We respond to every enquiry personally." />
      <section className="bg-paper py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4 space-y-8">
            <Eyebrow>Reach us</Eyebrow>
            {[[MapPin, FIRM.address, "contact-address"], [Phone, FIRM.phone, "contact-phone"], [Mail, FIRM.email, "contact-email"]].map(([Icon, v, id]) => (
              <div key={id} className="flex gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-ink text-brass shrink-0"><Icon className="w-5 h-5" /></span>
                <span data-testid={id} className="pt-2.5 text-ink/80">{v}</span>
              </div>
            ))}
          </Reveal>
          <div className="lg:col-span-8">
            {sent ? (
              <div data-testid="contact-success" className="rounded-[24px] bg-ink text-paper p-10 sm:p-16">
                <span className="grid place-items-center w-16 h-16 rounded-full bg-brass text-paper"><Check className="w-8 h-8" /></span>
                <p className="mt-8 font-display font-extrabold text-4xl">Thank you, {f.name.split(" ")[0]}.</p>
                <p className="mt-3 text-paper/75">Your enquiry has been received. We'll get back to you shortly.</p>
                <button data-testid="contact-send-another" onClick={() => { setSent(false); setF(empty); }} className="mt-8 text-brass underline underline-offset-4">Send another enquiry</button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
                <input data-testid="contact-name-input" required minLength={2} placeholder="Full name *" className={field} value={f.name} onChange={set("name")} />
                <input data-testid="contact-email-input" required type="email" placeholder="Email *" className={field} value={f.email} onChange={set("email")} />
                <input data-testid="contact-phone-input" placeholder="Phone" className={field} value={f.phone} onChange={set("phone")} />
                <input data-testid="contact-company-input" placeholder="Organisation" className={field} value={f.company} onChange={set("company")} />
                <select data-testid="contact-entity-select" className={`${field} sm:col-span-2`} value={f.entity_type} onChange={set("entity_type")}>
                  <option value="">Type of entity</option>
                  {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <textarea data-testid="contact-message-input" required minLength={5} rows={5} placeholder={f.message} className={`${field} sm:col-span-2 resize-none`} value={f.message} onChange={set("message")} />
                <div className="sm:col-span-2 pt-4">
                  <button data-testid="contact-submit-button" disabled={busy} className="rounded-full bg-ink text-paper px-9 py-4 font-semibold hover:bg-brass hover:text-paper transition-colors duration-300 disabled:opacity-60">
                    {busy ? "Sending…" : "Send enquiry"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
